/**
 * Choix d'un PACK du programme de l'élève (objectifs « Réviser un pack »,
 * migration 0514 — remplace le choix d'un chapitre). Packs de « Mon année »
 * (`get_my_progression`), groupés par matière dans l'ordre de l'année, avec la
 * maîtrise de chacun. Le pack du moment est mis en avant.
 */

import { Check } from 'lucide-react';
import { cn, PackIconGlyph } from '@jeraisonne/ui';
import {
  currentPackIndex,
  packPeriodLabel,
  sortPacks,
  subjectLabel,
} from '@jeraisonne/db/curriculum';
import { useMyProgression, type MyProgressionPack } from '@/lib/curriculum/api';

/** Packs groupés par matière, triés dans l'ordre de l'année ; index du pack du moment. */
export function groupPacksForPicker(
  packs: readonly MyProgressionPack[],
  today = new Date(),
): { subject: string; packs: MyProgressionPack[]; currentId: string | null }[] {
  const bySubject = new Map<string, MyProgressionPack[]>();
  for (const p of packs) {
    const list = bySubject.get(p.subject) ?? [];
    list.push(p);
    bySubject.set(p.subject, list);
  }
  return [...bySubject.entries()].map(([subject, list]) => {
    const sorted = sortPacks(list);
    const i = currentPackIndex(sorted, today);
    return { subject, packs: sorted, currentId: i >= 0 ? (sorted[i]?.id ?? null) : null };
  });
}

export function ObjectivePackPicker({
  selectedPackId,
  onSelect,
  disabled = false,
}: {
  selectedPackId: string | null;
  onSelect: (pack: MyProgressionPack) => void;
  disabled?: boolean;
}) {
  const { data, isLoading, error } = useMyProgression();

  if (isLoading) {
    return (
      <div className="space-y-2" aria-busy>
        {[0, 1, 2].map((i) => (
          <div key={i} className="bg-ink-100 h-16 animate-pulse rounded-2xl" />
        ))}
      </div>
    );
  }
  if (error) {
    return <p className="text-sm text-red-600">Impossible de charger tes packs.</p>;
  }
  const groups = groupPacksForPicker(data?.packs ?? []);
  if (groups.length === 0) {
    return (
      <p className="text-ink-500 rounded-2xl border border-dashed p-4 text-center text-sm">
        Aucun pack n’est encore publié pour ton niveau.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {groups.map((g) => (
        <section key={g.subject} className="space-y-2">
          {groups.length > 1 && (
            <h3 className="text-ink-500 text-xs font-semibold uppercase tracking-wider">
              {subjectLabel(g.subject)}
            </h3>
          )}
          <ul className="space-y-2">
            {g.packs.map((p) => {
              const selected = p.id === selectedPackId;
              const current = p.id === g.currentId;
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(p)}
                    disabled={disabled}
                    aria-pressed={selected}
                    className={cn(
                      'flex min-h-[3.5rem] w-full items-center gap-3 rounded-2xl border bg-white p-3 text-left transition active:scale-[0.99] disabled:opacity-60',
                      selected
                        ? 'border-primary-500 ring-primary-200 ring-2'
                        : 'border-ink-200 hover:border-primary-300',
                    )}
                  >
                    <span
                      aria-hidden
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white"
                      style={{ background: p.color ?? '#0566f2' }}
                    >
                      <PackIconGlyph icon={p.icon} className="h-5 w-5" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="text-ink-900 block truncate text-sm font-semibold">
                        {p.title}
                      </span>
                      <span className="text-ink-500 block text-xs">
                        {packPeriodLabel(p.start_month, p.end_month)} · {p.mastered_count}/
                        {p.card_count} SKILLCARDS maîtrisées
                        {current ? ' · en ce moment' : ''}
                      </span>
                    </span>
                    {selected && <Check className="text-primary-600 h-5 w-5 shrink-0" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
